import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { CHART_TOOLTIP_STYLE } from '../../utils/stats-utils'
import { cellKey, planCategories, categoryColorMap, type MatrixState } from './matrix-helpers'

interface PlanMinutesChartProps {
  matrix: MatrixState
}

/**
 * Stacked area chart shown under the plan matrix: X = weeks, Y = total work
 * minutes for the week (frequency × exercise duration), stacked by category.
 */
export function PlanMinutesChart({ matrix }: PlanMinutesChartProps) {
  const { t } = useTranslation()

  const categories = useMemo(() => planCategories(matrix), [matrix])
  const colorMap = useMemo(() => categoryColorMap(matrix), [matrix])

  const data = useMemo(() => {
    return matrix.weeks.map((week) => {
      const row: Record<string, string | number> = {
        label: t('trainingPlans.builder.weekShort', { number: week.week_number }),
      }
      for (const category of categories) row[category.id] = 0
      for (const exerciseRow of matrix.rows) {
        const categoryId = exerciseRow.exercise.category?.id
        if (!categoryId) continue
        const freq = matrix.frequencies[cellKey(week.week_number, exerciseRow.exercise_id)] ?? 0
        row[categoryId] = (row[categoryId] as number) + freq * (exerciseRow.exercise.duration_minutes ?? 0)
      }
      return row
    })
  }, [matrix.weeks, matrix.rows, matrix.frequencies, categories, t])

  const totalMinutes = useMemo(
    () => data.reduce((sum, row) => sum + categories.reduce((acc, c) => acc + (row[c.id] as number), 0), 0),
    [data, categories]
  )

  if (categories.length === 0 || matrix.weeks.length === 0 || totalMinutes === 0) return null

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-muted-foreground">{t('trainingPlans.builder.minutesChartTitle')}</p>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip {...CHART_TOOLTIP_STYLE} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {categories.map((category) => {
            const color = colorMap.get(category.id)
            return (
              <Area
                key={category.id}
                type="monotone"
                dataKey={category.id}
                name={category.name}
                stackId="minutes"
                stroke={color}
                fill={color}
                fillOpacity={0.55}
              />
            )
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
